import { useQuery } from '@apollo/client';
import { Dialog, DialogProps, Stack, styled } from '@mui/material';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import Contacts from './contacts/Contacts';
import CustomerFiles from './customerFiles/CustomerFiles';
import ExternalAccess from './externalAccess/ExternalAccess';
import General from './general/General';
import InternalAccess from './internalAccess/InternalAccess';
import SettingsModuleNavbar, { SettingsModuleType } from './SettingsModuleNavbar';
import Synchronizations from './synchronizations/Synchronizations';
import UserCustomerFileRelationProvider, {
	useUserCustomerFileRelationContext,
} from './UserCustomerFileRelationProvider';
import SettingsWidgets from './widgets/SettingsWidgets';
import { useConfirmationDialogContext } from '../../components/ConfirmationDialogProvider';
import { SettingsDialogContext } from '../../hooks/useSettingsDialog';
import modules from '../../modules/modules';
import { PermissionEnum } from '../../types/permission';
import { FETCH_SETTING_MODULES, FetchSettingModulesType } from '../api/settingModule';
import { useSession } from '../components/session/SessionContext';
import useCustomRouter from '../hooks/useCustomRouter';
import { UserTypeEnum } from '../types/user';

const generalModules: SettingsModuleType[] = [
	{
		code: 'general',
		label: 'Général',
		permissionCodes: [PermissionEnum.THEME_MANAGE],
		component: <General />,
	},
	{
		code: 'customerFiles',
		label: 'Dossiers clients',
		permissionCodes: [PermissionEnum.DOMAIN_MANAGE, PermissionEnum.CUSTOMER_FILE_MANAGE],
		component: <CustomerFiles />,
	},
	{
		code: 'internalAccess',
		label: 'Accès cabinet',
		permissionCodes: [PermissionEnum.INTERNAL_GROUP_MANAGE, PermissionEnum.CUSTOMER_FILE_INTERNAL_USER_MANAGE],
		component: <InternalAccess />,
	},
	{
		code: 'externalAccess',
		label: 'Accès entreprise',
		permissionCodes: [PermissionEnum.EXTERNAL_GROUP_MANAGE, PermissionEnum.CUSTOMER_FILE_EXTERNAL_USER_MANAGE],
		component: <ExternalAccess />,
	},
	{
		code: 'contacts',
		label: 'Contacts',
		permissionCodes: [PermissionEnum.CUSTOMER_FILE_MANAGE],
		component: <Contacts />,
	},
	{
		code: 'widgets',
		label: 'Widgets',
		permissionCodes: [PermissionEnum.WIDGET_DOMAIN_MANAGE, PermissionEnum.WIDGET_CUSTOMER_FILE_MANAGE],
		component: <SettingsWidgets />,
	},
	{
		code: 'synchronizations',
		label: 'Synchronisations',
		permissionCodes: [PermissionEnum.ACCOUNTING_SYNCHRONIZE],
		component: <Synchronizations />,
	},
];

type SettingsDialogPage = {
	component: ReactNode;
};

const StyledDialog = styled(({ className, ...props }: DialogProps) => (
	<Dialog {...props} classes={{ paper: className }} />
))(({ theme: { shape } }) => ({
	height: '80%',
	minWidth: '80%',

	flexDirection: 'row',

	borderRadius: shape.borderRadius * 1.5,

	overflow: 'hidden',
}));

const SettingsDialog = () => {
	const {
		checkPermission,
		user: { type: sessionUserType, isInternalAdministrator },
	} = useSession();
	const { userCustomerFileRelations, setUserCustomerFileRelations } = useUserCustomerFileRelationContext();
	const { confirmCloseDialog } = useConfirmationDialogContext();

	const { query, replace } = useCustomRouter();
	const isSettingsDialogOpen = 'settings' in query;

	const [pages, setPages] = useState<SettingsDialogPage[]>([]);
	const [activeModule, setActiveModule] = useState<SettingsModuleType>();
	const [hasDirtyForm, setHasDirtyForm] = useState(false);
	const [closeConfirmationTitle, setCloseConfirmationTitle] = useState<string>();

	const { data: settingModulesData } = useQuery<FetchSettingModulesType>(FETCH_SETTING_MODULES, {
		skip: !isSettingsDialogOpen,
	});

	const page = useMemo(() => {
		const [lastPage] = pages.slice(-1);

		return lastPage;
	}, [pages]);

	const allowedGeneralModules = useMemo(
		() =>
			generalModules.filter(({ code, permissionCodes }) => {
				if (permissionCodes.length === 0 || (sessionUserType === UserTypeEnum.EXTERNAL && code === 'externalAccess')) {
					return true;
				}

				return permissionCodes.some((permissionCode) => checkPermission(permissionCode));
			}),
		[checkPermission, sessionUserType],
	);

	const allowedModules: SettingsModuleType[] = useMemo(() => {
		const { settingModules: fetchedSettingModules = [] } = settingModulesData ?? {};

		return fetchedSettingModules.reduce((acc, { code }) => {
			const storedModule = modules.find(({ code: moduleCode, settings }) =>
				code === 'sign' ? moduleCode === code && settings && isInternalAdministrator : moduleCode === code && settings,
			);

			if (!storedModule) {
				return acc;
			}

			const { name, settings } = storedModule;

			return [
				...acc,
				{
					label: name,
					permissionCodes: [],
					component: settings,
					code,
				},
			];
		}, [] as SettingsModuleType[]);
	}, [isInternalAdministrator, settingModulesData]);

	const handleChangeActiveModule = useCallback(
		(module: SettingsModuleType) => {
			setHasDirtyForm(false);
			const { component } = module;

			setPages([{ component }]);
			setActiveModule(module);

			if (userCustomerFileRelations.length > 0) {
				setUserCustomerFileRelations([]);
			}
		},
		[setUserCustomerFileRelations, userCustomerFileRelations.length],
	);

	const closeDialog = useCallback(() => {
		const updatedQuery = query;
		delete updatedQuery.settings;

		replace({
			query: updatedQuery,
		}).catch((error) => {
			throw error;
		});

		setActiveModule(undefined);
		setPages([]);

		if (userCustomerFileRelations.length > 0) {
			setUserCustomerFileRelations([]);
		}

		setHasDirtyForm(false);
		setCloseConfirmationTitle(undefined);
	}, [query, replace, setUserCustomerFileRelations, userCustomerFileRelations.length]);

	const handleCloseEvent = useCallback(() => {
		(async () => {
			if (hasDirtyForm) {
				const isCloseConfirmed = await confirmCloseDialog({
					title: closeConfirmationTitle,
				});

				if (!isCloseConfirmed) return;
				closeDialog();
			}

			closeDialog();
		})().catch((closeDialogError) => {
			throw closeDialogError;
		});
	}, [closeConfirmationTitle, closeDialog, confirmCloseDialog, hasDirtyForm]);

	const contextValue = useMemo(
		() => ({
			isFirstPage: pages.length <= 1,
			push: (component: ReactNode) => {
				setPages((previousValue) => [
					...previousValue,
					{
						key: uuid(),
						component,
					},
				]);
				setHasDirtyForm(false);
				setCloseConfirmationTitle(undefined);
			},
			replace: (component: ReactNode) => {
				setPages((previousValue) => [
					...previousValue.slice(0, -1),
					{
						key: uuid(),
						component,
					},
				]);
				setHasDirtyForm(false);
				setCloseConfirmationTitle(undefined);
			},
			back: () => {
				setPages((previousValue) => previousValue.slice(0, -1));
				setHasDirtyForm(false);
				setCloseConfirmationTitle(undefined);
			},
			close: handleCloseEvent,
			onHasDirtyFormChange: (value: boolean) => setHasDirtyForm(value),
			onCloseConfirmationTitleChange: (value?: string) => setCloseConfirmationTitle(value),
		}),
		[handleCloseEvent, pages],
	);

	useEffect(() => {
		if (activeModule) return;

		const [firstModule] = [...allowedGeneralModules, ...allowedModules];
		if (!firstModule) return;

		setPages([{ component: firstModule.component }]);
		setActiveModule(firstModule);
	}, [activeModule, allowedGeneralModules, allowedModules]);

	return (
		<SettingsDialogContext.Provider value={contextValue}>
			<StyledDialog open={isSettingsDialogOpen} onClose={handleCloseEvent} disableEnforceFocus>
				<SettingsModuleNavbar
					activeModule={activeModule}
					onChangeActiveModule={handleChangeActiveModule}
					generalModules={allowedGeneralModules}
					modules={allowedModules}
				/>
				<Stack flex={1} width="calc(100% - 200px)">
					{page && page.component}
				</Stack>
			</StyledDialog>
		</SettingsDialogContext.Provider>
	);
};

const SettingsDialogWithUserCustomerFileRelationProvider = () => (
	<UserCustomerFileRelationProvider>
		<SettingsDialog />
	</UserCustomerFileRelationProvider>
);

export default SettingsDialogWithUserCustomerFileRelationProvider;
